import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDataChannel } from "@livekit/components-react";
import { toast } from "sonner";
import { SuiteApiError } from "@weco/suite-client";
import { videoService } from "../api/video.api";
import type { LobbyParticipant } from "../api/types/video";
import { PARTICIPANT_WAITING_TYPE, parseRoomDataMessage } from "../lib/roomDataMessages";

export interface UseWaitingGuestsResult {
  waitingParticipants: LobbyParticipant[];
  waitingCount: number;
  admit: (participantId: string) => Promise<void>;
  deny: (participantId: string) => Promise<void>;
}

/**
 * Tracks guests waiting for lobby admission.
 *
 * Must be called inside a <LiveKitRoom> provider. When `enabled` is false the
 * hook still calls all its React hooks (rules of hooks) but returns empty state
 * and skips all network requests.
 *
 * Signals: backend emits `{ type: "participantWaiting" }` via the LiveKit data
 * channel (no topic) each time a guest requests entry. The hook also fetches on
 * mount so pre-existing waiters are surfaced when the host joins late.
 */
export function useWaitingGuests(
  roomId: string,
  lkToken: string,
  enabled: boolean,
): UseWaitingGuestsResult {
  const decoder = useMemo(() => new TextDecoder(), []);
  const [participants, setParticipants] = useState<LobbyParticipant[]>([]);
  const fetchingRef = useRef(false);

  const fetchWaiting = useCallback(async () => {
    if (!enabled || !roomId || !lkToken) return;
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const res = await videoService.getWaitingParticipants(roomId, lkToken);
      setParticipants(res.participants);
    } catch (err) {
      // 403 = the server no longer lets this caller manage the list (the
      // admit policy was switched off). Hide it rather than keep showing a
      // stale list whose buttons would all be refused (#1850).
      if (err instanceof SuiteApiError && err.code === 403) {
        setParticipants([]);
        return;
      }
      console.error("useWaitingGuests: fetchWaiting failed:", err);
    } finally {
      fetchingRef.current = false;
    }
  }, [enabled, roomId, lkToken]);

  // Initial fetch + 5-second poll as fallback when the hook is active.
  // The data channel event is the fast path; polling catches cases where
  // notify_participants fails (LiveKit unreachable from backend) or the
  // host joined after the guest already queued.
  useEffect(() => {
    if (!enabled) return;
    void fetchWaiting();
    const id = setInterval(() => { void fetchWaiting(); }, 5000);
    return () => clearInterval(id);
  }, [enabled, fetchWaiting]);

  // Listen for lobby join request notifications on the data channel.
  // Backend sends `{ type: "participantWaiting" }` without a topic — the
  // no-argument overload of useDataChannel receives all topicless messages.
  useDataChannel((msg) => {
    if (!enabled) return;
    // Non-JSON broadcast messages from other hooks parse to null.
    if (parseRoomDataMessage(decoder, msg.payload)?.type === PARTICIPANT_WAITING_TYPE) {
      void fetchWaiting();
    }
  });

  const admit = useCallback(
    async (participantId: string) => {
      try {
        await videoService.admitParticipant(roomId, participantId, true, lkToken);
        await fetchWaiting();
      } catch (err) {
        console.error("useWaitingGuests: admit failed:", err);
        toast.error("Could not admit participant");
      }
    },
    [roomId, lkToken, fetchWaiting],
  );

  const deny = useCallback(
    async (participantId: string) => {
      try {
        await videoService.admitParticipant(roomId, participantId, false, lkToken);
        await fetchWaiting();
      } catch (err) {
        console.error("useWaitingGuests: deny failed:", err);
        toast.error("Could not deny participant");
      }
    },
    [roomId, lkToken, fetchWaiting],
  );

  return {
    waitingParticipants: enabled ? participants : [],
    waitingCount: enabled ? participants.length : 0,
    admit,
    deny,
  };
}
